import { useCallback, useEffect, useRef } from 'react';

/**
 * Keeps only the newest of a component's async checks, each identified by a
 * key: the members' interaction check in SupplementsTable, keyed by the
 * selection it checks, which re-runs on every change and can keep asking the
 * backend for ~104 s.
 *
 * `startIfNew(key)` returns an AbortSignal for a new check, aborting the one
 * before it, or null when the newest check is already for `key`. `cancel()`
 * aborts the current check and forgets its key, as unmount does, so the next
 * `startIfNew` always starts. Forgetting on unmount matters under React 18
 * StrictMode, which unmounts and remounts the same instance in development:
 * the remount has to start the check its unmount cancelled.
 */
export default function useLatestCheck() {
  const keyRef = useRef(null);
  const controllerRef = useRef(null);

  const cancel = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    keyRef.current = null;
  }, []);

  const startIfNew = useCallback((key) => {
    if (keyRef.current === key) return null;
    controllerRef.current?.abort();
    controllerRef.current = new AbortController();
    keyRef.current = key;
    return controllerRef.current.signal;
  }, []);

  useEffect(() => cancel, [cancel]);

  return { startIfNew, cancel };
}
